import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const publicLinks = [
  { to: '/', label: 'الرئيسية' },
  { to: '/martyrs', label: 'الشهداء' },
  { to: '/biography', label: 'السيرة' },
  { to: '/writings', label: 'كتب عنه' },
  { to: '/poems', label: 'القصائد' },
  {
    to: '/gallery',
    label: 'المعرض',
    children: [
      { to: '/gallery/photos', label: 'الصور' },
      { to: '/gallery/videos', label: 'الفيديوهات' }
    ]
  },
  { to: '/guestbook', label: 'سجل الزوار' },
  { to: '/contact', label: 'تواصل' }
];

const adminLinks = [
  { to: '/admin/dashboard', label: 'لوحة التحكم' },
  { to: '/admin/add-martyr', label: 'إضافة شهيد' },
  { to: '/admin/writings', label: 'كتب عنه' },
  { to: '/admin/poems', label: 'القصائد' },
  { to: '/admin/media', label: 'الوسائط' },
  { to: '/admin/tributes', label: 'الرثاء' },
  { to: '/admin/videos', label: 'فيديوهات الموقع' }
];

const Header = () => {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isLoggedIn = Boolean(localStorage.getItem('adminToken'));
  const [open, setOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const links = isAdminPage ? adminLinks : publicLinks;

  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.href = '/admin/login';
  };

  return (
    <header className="sticky top-0 z-40 border-b border-gold-500/15 bg-stone-50/85 backdrop-blur-xl dark:bg-stone-950/85">
      <div className="container-responsive flex h-16 items-center justify-between gap-4 md:h-[4.5rem]">
        <Link to={isAdminPage ? '/admin/dashboard' : '/'} className="flex shrink-0 items-center gap-3">
          <img src="/logo.png" alt="أرشيف الشهداء" className="h-10 w-10 rounded-xl shadow-soft md:h-11 md:w-11" />
          <span>
            <p className="text-[10px] tracking-[0.28em] text-gold-700 dark:text-gold-400">
              {isAdminPage ? 'لوحة الإدارة' : 'متحف رقمي'}
            </p>
            <h1 className="text-lg font-bold text-primary-900 dark:text-white md:text-xl">
              {isAdminPage ? 'إدارة الأرشيف' : 'أرشيف الشهداء'}
            </h1>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex">
          {links.map((link) => (
            link.children ? (
              <div
                key={link.to}
                className="relative"
                onMouseEnter={() => setGalleryOpen(true)}
                onMouseLeave={() => setGalleryOpen(false)}
              >
                <Link
                  to={link.to}
                  className={`rounded-full px-3 py-2 text-sm transition-colors ${
                    isActive(link.to)
                      ? 'bg-primary-900 text-white dark:bg-gold-600'
                      : 'text-stone-600 hover:bg-stone-200/70 dark:text-stone-300 dark:hover:bg-stone-800'
                  }`}
                >
                  {link.label}
                </Link>
                {galleryOpen && (
                  <div className="absolute end-0 top-full z-50 min-w-[11rem] pt-2">
                    <div className="rounded-2xl border border-stone-200 bg-white py-2 shadow-large dark:border-stone-700 dark:bg-stone-900">
                    {link.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        onClick={() => setGalleryOpen(false)}
                        className={`block px-4 py-2 text-right text-sm ${
                          location.pathname.startsWith(child.to)
                            ? 'bg-primary-900 text-white'
                            : 'text-stone-700 hover:bg-stone-100 dark:text-stone-200 dark:hover:bg-stone-800'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-full px-3 py-2 text-sm transition-colors ${
                  isActive(link.to)
                    ? 'bg-primary-900 text-white dark:bg-gold-600'
                    : 'text-stone-600 hover:bg-stone-200/70 dark:text-stone-300 dark:hover:bg-stone-800'
                }`}
              >
                {link.label}
              </Link>
            )
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-full border border-stone-200 p-2 text-sm dark:border-stone-700"
            aria-label="تبديل المظهر"
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          {!isAdminPage && (
            <Link to="/admin/login" className="hidden rounded-full bg-primary-900 px-4 py-2 text-sm text-white md:inline-flex">
              المدير
            </Link>
          )}
          {isAdminPage && isLoggedIn && (
            <>
              <Link to="/" className="hidden text-sm text-stone-500 md:inline">عرض الموقع</Link>
              <button type="button" onClick={handleLogout} className="hidden rounded-full bg-error-600 px-3 py-2 text-sm text-white md:inline">
                خروج
              </button>
            </>
          )}
          <button
            type="button"
            className="rounded-lg p-2 xl:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="القائمة"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {open ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-stone-200 bg-stone-50 px-4 py-4 dark:border-stone-800 dark:bg-stone-950 xl:hidden">
          <div className="flex flex-col gap-1">
            {links.map((link) => (
              link.children ? (
                <div key={link.to} className="flex flex-col">
                  <Link
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className={`rounded-xl px-4 py-3 text-right ${
                      isActive(link.to) ? 'bg-primary-900 text-white' : 'text-stone-700 dark:text-stone-200'
                    }`}
                  >
                    {link.label}
                  </Link>
                  {link.children.map((child) => (
                    <Link
                      key={child.to}
                      to={child.to}
                      onClick={() => setOpen(false)}
                      className={`rounded-xl px-8 py-2 text-right text-sm ${
                        location.pathname.startsWith(child.to)
                          ? 'text-primary-800 font-semibold'
                          : 'text-stone-500 dark:text-stone-400'
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={`rounded-xl px-4 py-3 text-right ${
                    isActive(link.to) ? 'bg-primary-900 text-white' : 'text-stone-700 dark:text-stone-200'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            {!isAdminPage && (
              <>
                <Link to="/timeline" onClick={() => setOpen(false)} className="rounded-xl px-4 py-3">الخط الزمني</Link>
                <Link to="/admin/login" onClick={() => setOpen(false)} className="rounded-xl bg-primary-900 px-4 py-3 text-white">المدير</Link>
              </>
            )}
            {isAdminPage && isLoggedIn && (
              <button type="button" onClick={handleLogout} className="rounded-xl bg-error-600 px-4 py-3 text-right text-white">
                تسجيل الخروج
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
